/** SUNTRONIX ONE — ONE CONTROL CENTRE.
 *  Platform governance roll-up (real status, never fabricated) + Final
 *  Production-Control Certification with PDF export. Read-only aggregation
 *  over existing modules; deep-links back to each. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Activity, DatabaseBackup, Boxes, FileText, Users, ScrollText,
  Rocket, LifeBuoy, Plug, Wallet, Gauge, RefreshCw, Download, ChevronRight,
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTION_ICON = {
  system_health: Gauge, security: ShieldCheck, backup: DatabaseBackup,
  data_quality: Activity, integrations: Plug, finance: Wallet, stock: Boxes,
  documents: FileText, permissions: Users, audit: ScrollText, releases: Rocket,
  continuity: LifeBuoy,
};
const STATUS = {
  HEALTHY: { tone: "text-emerald-700 bg-emerald-50 ring-emerald-200", dot: "bg-emerald-500", Icon: CheckCircle2, label: "Healthy" },
  WARNING: { tone: "text-amber-700 bg-amber-50 ring-amber-200", dot: "bg-amber-500", Icon: AlertTriangle, label: "Warning" },
  ACTION_REQUIRED: { tone: "text-rose-700 bg-rose-50 ring-rose-200", dot: "bg-rose-500", Icon: AlertTriangle, label: "Action required" },
  CRITICAL: { tone: "text-rose-800 bg-rose-100 ring-rose-300", dot: "bg-rose-600", Icon: XCircle, label: "Critical" },
  NOT_EVALUATED: { tone: "text-slate-500 bg-slate-50 ring-slate-200", dot: "bg-slate-400", Icon: MinusCircle, label: "Not evaluated" },
};
const DOMAIN_TONE = {
  PASS: "text-emerald-700 bg-emerald-50", PASS_WITH_WARNING: "text-amber-700 bg-amber-50",
  ACTION_REQUIRED: "text-rose-700 bg-rose-50", NOT_EVALUATED: "text-slate-500 bg-slate-50",
};

export default function OneControlCentre() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certLoading, setCertLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/control-centre/overview"); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runCert = async () => {
    setCertLoading(true);
    try { const { data } = await api.get("/control-centre/certification"); setCert(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCertLoading(false); }
  };
  const downloadPdf = async () => {
    try {
      const { data } = await api.get("/control-centre/certification/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "SUNTRONIX-ONE-Certification.pdf";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("PDF download failed"); }
  };

  const overall = data ? STATUS[data.overall_status] || STATUS.NOT_EVALUATED : STATUS.NOT_EVALUATED;
  const certified = cert?.final_status?.endsWith("CERTIFIED");

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6" data-testid="one-control-centre">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 flex items-center gap-2">
            <Gauge className="h-3.5 w-3.5" /> Administration
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ONE Control Centre</h1>
          <p className="text-[12px] text-slate-500 mt-1">Live platform governance — security, backup, data quality, finance, stock, documents, permissions, releases & continuity. Statuses are computed from real signals, never fabricated.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="cc-refresh"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm p-8">Loading platform signals…</div>
      ) : !data ? null : (
        <>
          <div className={`rounded-xl ring-1 p-5 flex items-center gap-4 ${overall.tone}`} data-testid="cc-overall">
            <overall.Icon className="h-8 w-8" />
            <div className="flex-1">
              <div className="text-lg font-bold">Overall platform status: {overall.label}</div>
              <div className="text-[12px] opacity-80">
                {data.counts.HEALTHY} healthy · {data.counts.WARNING} warning · {data.counts.ACTION_REQUIRED} action required · {data.counts.CRITICAL} critical
              </div>
            </div>
            <div className="text-[10px] opacity-70">as of {String(data.generated_at).slice(0, 16).replace("T", " ")}</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cc-sections">
            {data.sections.map((s) => {
              const Icon = SECTION_ICON[s.key] || Activity;
              const st = STATUS[s.status] || STATUS.NOT_EVALUATED;
              return (
                <button key={s.key} data-testid={`cc-section-${s.key}`}
                  onClick={() => s.link && nav(s.link)}
                  className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-slate-300 transition-all group">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-700"><Icon className="h-5 w-5" /><span className="font-semibold">{s.label}</span></div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${st.tone}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}
                    </span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-2 min-h-[32px]">{s.summary}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                      {Object.entries(s.metrics || {}).slice(0, 3).map(([k, v]) => (
                        <span key={k}>{k.replaceAll("_", " ")}: <b className="text-slate-600">{String(v)}</b></span>
                      ))}
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Certification panel */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="cc-certification">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-slate-700" />
                <div>
                  <div className="font-semibold text-slate-900">Final Production-Control Certification</div>
                  <div className="text-[12px] text-slate-500">Runs live checks across ~28 governance domains and reports honest PASS / WARNING / ACTION REQUIRED.</div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={runCert} disabled={certLoading} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="cc-run-cert">
                  {certLoading ? "Running…" : "Run certification"}
                </Button>
                <Button variant="outline" onClick={downloadPdf} data-testid="cc-download-pdf"><Download className="h-4 w-4 mr-1" />PDF</Button>
              </div>
            </div>

            {cert && (
              <div className="mt-4">
                <div className={`rounded-lg p-3 mb-3 font-bold text-center ring-1 ${certified ? "text-emerald-700 bg-emerald-50 ring-emerald-200" : "text-rose-700 bg-rose-50 ring-rose-200"}`} data-testid="cc-final-status">
                  {cert.final_status}
                  <span className="block text-[11px] font-normal opacity-80 mt-0.5">{cert.pass_count} pass · {cert.warning_count} warning · {cert.action_required_count} action required</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  {cert.domains.map((d) => (
                    <div key={d.domain} className="flex items-center justify-between py-1 border-b border-slate-100 text-[12px]" data-testid="cc-cert-domain">
                      <span className="text-slate-700 font-medium">{d.domain}</span>
                      <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${DOMAIN_TONE[d.status] || "text-slate-500 bg-slate-50"}`} title={d.detail}>
                        {d.status.replaceAll("_", " ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
